import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Drawer,
  drawerClasses,
  IconButton,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type ToggleButtonProps,
} from "~/common/mui";
import { Close } from "@mui/icons-material";

import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { ReduxFilterSection } from "~/features/search/components/Filters/ReduxFilterSection";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { applyAllFiltersAction, clearAll } from "~/features/search/redux/actions";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { useForm } from "react-hook-form";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";
import { selectAppliedTechFilterValues } from "~/features/search/redux/searchSelectors";
import { useSelectAppliedFilters } from "../../Filters/components/AppliedFilters/useSelectAppliedFilters";

export const FilterButton = ({ children, onClick, ...props }: Omit<ToggleButtonProps, "value">) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const isMinTablet = useIsMinTabletSize();
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    if (isDrawerOpen) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, isDrawerOpen]);

  const pageType = useSearchPageType();

  const handleClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
    handleClose();
  };

  const applyCb = methods.handleSubmit(onSubmit);
  const breadcrumbLength = useSelectAppliedFilters().length;

  let label = children;
  if (!label) {
    label = t("notificationsPage.filters.title");
    if (breadcrumbLength) {
      label += ` (${breadcrumbLength})`;
    }
  }

  return (
    <>
      <ToggleButtonGroup value={breadcrumbLength ? "1" : ""}>
        <ToggleButton
          value="1"
          sx={{ color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
          onClick={(e, v) => {
            setIsDrawerOpen(true);
            onClick?.(e, v);
          }}
          {...props}
        >
          <FilterAltOutlinedIcon sx={{ my: "-2px" }} />
          {label}
        </ToggleButton>
      </ToggleButtonGroup>

      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography
              variant="titleLarge"
              sx={{
                fontWeight: "bold",
              }}
            >
              Filter
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <form onSubmit={applyCb}>
            <ReduxFilterSection
              titleFontVariant={"titleMedium"}
              hideTechFilterButtons
              methods={methods}
              onSubmit={onSubmit}
            />
          </form>
        </DrawerContent>

        <DrawerActions>
          <Button variant="text" fullWidth onClick={handleClearAll}>
            {t("searchPage.refinement.button.clearAll")}
          </Button>
          <Button fullWidth onClick={applyCb} disabled={!methods.formState.isDirty}>
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "90vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.up("tablet")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  position: "sticky",
  top: "0",
  paddingTop: "16px",
  zIndex: 5,
  background: theme.palette.surface.bright,
}));

const DrawerContent = styled(DialogContent)(({ theme }) => ({
  padding: "0 16px 16px 16px",
  backgroundColor: theme.palette.surface.bright,
}));

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
  zIndex: 1,
});
